import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'

import type { EventDetailExhibitor, EventDetailExhibitorRep } from '../types/event-detail'
import type { EventsManagementExhibitorsMeta } from '../types/events-management'

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

const asNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0)

const titleCase = (value: string): string =>
  value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')

const tierName = (tierId: string, tiers: readonly unknown[]): string => {
  for (const entry of tiers) {
    const tier = asRecord(entry)
    if (!tier || asString(tier.id) !== tierId) continue
    return asString(tier.name) || titleCase(tierId)
  }
  return tierId ? titleCase(tierId) : ''
}

const mapExhibitor = (value: unknown, tiers: readonly unknown[]): EventDetailExhibitor | undefined => {
  const exhibitor = asRecord(value)
  if (!exhibitor) return undefined
  if (exhibitor.deleted === true) return undefined
  if (asString(exhibitor.visibility) !== 'visible') return undefined

  const id = asString(exhibitor.id)
  const name = asString(exhibitor.name)
  if (!id || !name) return undefined

  const booth = asString(asRecord(exhibitor.booth)?.number)
  const tag = tierName(asString(exhibitor.tierId), tiers)
  const description = asString(exhibitor.description)
  const websiteUrl = asString(exhibitor.websiteUrl)
  const logoUrl = asString(exhibitor.logoUrl)

  return {
    id,
    name,
    booth,
    initial: name.charAt(0).toUpperCase(),
    ...(tag ? { tag } : {}),
    ...(description ? { description } : {}),
    ...(isExternalHttpsRedirect(websiteUrl) ? { websiteUrl } : {}),
    ...(isExternalHttpsRedirect(logoUrl) ? { logoUrl } : {}),
    showAboutAndLinks: exhibitor.showAboutAndLinks !== false,
    showTeamAtEvent: exhibitor.showTeamAtEvent !== false,
  }
}

export const mapEventExhibitors = (rows: readonly unknown[], meta: EventsManagementExhibitorsMeta | undefined): EventDetailExhibitor[] => {
  const tiers = meta?.tiers ?? []
  return [...rows]
    .map((exhibitor, index) => ({ exhibitor, order: asNumber(asRecord(exhibitor)?.sortOrder), index }))
    .sort((left, right) => (left.order !== right.order ? left.order - right.order : left.index - right.index))
    .map(({ exhibitor }) => mapExhibitor(exhibitor, tiers))
    .filter((exhibitor): exhibitor is EventDetailExhibitor => Boolean(exhibitor))
}

export const mapEventExhibitorMembers = (rows: readonly unknown[]): EventDetailExhibitorRep[] =>
  rows.flatMap(value => {
    const member = asRecord(value)
    if (!member || member.showPublicly === false) return []

    const id = asString(member.id)
    const name = asString(member.name)
    if (!id || !name) return []

    return [{ id, name, role: asString(member.jobTitle) }]
  })

export const exhibitorWebsiteLabel = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
